import React, { useEffect, useState, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import * as walletService from '../../services/walletService'
import { getAffiliateMembers } from '../../services/affiliateService'
import type { AffiliateOutletContext } from './ModernAffiliateLayout'
import type { AffiliateMemberItem } from '../../services/affiliateService'

function formatJoinDate(joinedAt: string): string {
  try {
    const d = new Date(joinedAt)
    return Number.isNaN(d.getTime()) ? joinedAt : d.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return joinedAt
  }
}

function maskMember(raw: string): string {
  const s = String(raw || '').trim()
  if (!s) return 'สมาชิก'
  if (s.length < 4) return s
  if (/^\d+$/.test(s)) {
    if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
    return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
  }
  if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
  return s.slice(0, 2) + '****' + s.slice(-3)
}

const AffiliateMemberPage: React.FC = () => {
  useOutletContext<AffiliateOutletContext>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [members, setMembers] = useState<AffiliateMemberItem[]>([])
  const [searchKeyword, setSearchKeyword] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const list = await getAffiliateMembers()
        if (!cancelled) setMembers(list)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const filteredMembers = useMemo(() => {
    if (!searchKeyword.trim()) return members
    const q = searchKeyword.trim().toLowerCase()
    return members.filter(
      (m) =>
        (m.username || '').toLowerCase().includes(q) ||
        (m.displayName || '').toLowerCase().includes(q) ||
        maskMember(m.username || '').toLowerCase().includes(q)
    )
  }, [members, searchKeyword])

  const totalBetAmount = 0

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2 c_tertiary">กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="text-center py-4">
        <p className="c_primary mb-0">{error}</p>
      </div>
    )
  }

  return (
    <>
      <div className="row mx__1">
        <div className="col-6 px-1 mb-2">
          <div className="border_5 bdc_primary overflow-hidden">
            <div className="bg_primary c_tertiary text-center py-1">จำนวนสมาชิก (คน)</div>
            <div className="bg_secondary p-2">
              <h4 className="text-center c_secondary mb-0">{members.length}</h4>
            </div>
          </div>
        </div>
        <div className="col-6 px-1 mb-2">
          <div className="border_5 bdc_primary overflow-hidden">
            <div className="bg_primary c_tertiary text-center py-1">ยอดแทงรวม (บาท)</div>
            <div className="bg_secondary p-2">
              <h4 className="text-center c_primary mb-0">{walletService.formatCurrency(totalBetAmount)}</h4>
            </div>
          </div>
        </div>
      </div>

      <div className="border_5 bdc_primary bg_op_secondary p-3 overflow-hidden my-2 text-start">
        <div className="c_tertiary mb-2">
          <h5 className="mb-0">สมาชิก</h5>
        </div>
        <div className="d-flex align-items-stretch" style={{ gap: 10, flexWrap: 'nowrap' }}>
          <input
            type="text"
            className="form-control bg_secondary c_secondary"
            placeholder="ค้นหา username ที่ถูกปิดบัง..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ flex: 1, minWidth: 0, borderRadius: 12, minHeight: 46 }}
          />
          <button
            type="button"
            className="bgg-1 c-white border-0 px-3 px-md-4 font_md"
            style={{ minWidth: 110, borderRadius: 12, whiteSpace: 'nowrap', flexShrink: 0 }}
            onClick={() => setSearchKeyword(searchKeyword.trim())}
          >
            <i className="fas fa-search me-2"></i>
            ค้นหา
          </button>
        </div>
      </div>

      <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
        {filteredMembers.length === 0 ? (
          <div className="col-12 px-1">
            <div className="border_5 bg_secondary py-5 overflow-hidden">
              <h4 className="text-center c_primary mb-0">ไม่มีรายการ</h4>
            </div>
          </div>
        ) : (
          filteredMembers.map((member, index) => (
            <div key={`${member.username}-${index}`} className="col-12 col-md-6 px-1">
              <div className="border_5 bdc_primary bg_op_secondary p-3 overflow-hidden">
                <div className="c_tertiary mb-2">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="text-start">
                      <h4 className="font_md c_primary mb-0">{maskMember(member.username || member.displayName || 'สมาชิก')}</h4>
                      <small className="c_secondary" style={{ opacity: 0.82 }}>แสดง username แบบปิดบังบางส่วน</small>
                    </div>
                    <p className="c_tertiary bg_primary text-center rounded-1 mb-0 px-3">#{index + 1}</p>
                  </div>
                  <small className="d-block">
                    สมัครเมื่อวันที่ {formatJoinDate(member.joinedAt)}{' '}
                    <span className="c_tertiary border_30 text-nowrap px-2 bg_gd_quaternary">
                      {member.status || 'สมาชิกปกติ'}
                    </span>
                  </small>
                </div>
                <div className="row mx__1">
                  <div className="col px-1">
                    <div className="bg_primary c_tertiary border_5 text-center py-2">
                      <h6 className="mb-0">เดิมพันทั้งหมด</h6>
                      <h5 className="font_md mb-0">{walletService.formatCurrency(0)}</h5>
                    </div>
                  </div>
                  <div className="col px-1">
                    <div className="bg_secondary c_secondary border_5 text-center py-2">
                      <h6 className="mb-0">คอมมิชชั่น</h6>
                      <h5 className="font_md mb-0">{walletService.formatCurrency(0)}</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  )
}

export default AffiliateMemberPage
